import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Calendar, momentLocalizer, type EventProps } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/pt-br';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { Button } from '../../components/Shared/Button';
import { Modal } from '../../components/Shared/Modal';
import { Input } from '../../components/Shared/Input';
import { useAuth } from '../../contexts/AuthContext';
import {
  calendarService,
  type CalendarBreakDto,
  type CalendarDto,
  type CalendarEventDto,
} from '../../services/calendarService';

moment.locale('pt-br');
const localizer = momentLocalizer(moment);

const WORK_DAY_KEYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const;
const WORK_DAY_LABELS: Record<(typeof WORK_DAY_KEYS)[number], string> = {
  Mon: 'Seg',
  Tue: 'Ter',
  Wed: 'Qua',
  Thu: 'Qui',
  Fri: 'Sex',
  Sat: 'Sáb',
  Sun: 'Dom',
};

// SQL Server-style weekday numbers: 1=Sun, 2=Mon, 3=Tue, 4=Wed, 5=Thu, 6=Fri, 7=Sat
const NUMERIC_TO_WORK_DAY_KEY: Record<string, (typeof WORK_DAY_KEYS)[number]> = {
  '1': 'Sun',
  '2': 'Mon',
  '3': 'Tue',
  '4': 'Wed',
  '5': 'Thu',
  '6': 'Fri',
  '7': 'Sat',
};

function normalizeWorkDayToken(token: string): string {
  if ((WORK_DAY_KEYS as readonly string[]).includes(token)) return token;
  return NUMERIC_TO_WORK_DAY_KEY[token] ?? token;
}

function formatWorkDaysDisplay(csv: string | undefined): string {
  if (!csv) return '—';
  const parts = csv
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map((token) => {
      const key = normalizeWorkDayToken(token);
      return WORK_DAY_LABELS[key as (typeof WORK_DAY_KEYS)[number]] ?? key;
    });
  return parts.length > 0 ? parts.join(', ') : '—';
}

const BREAK_DAY_OPTIONS: { value: string; label: string }[] = [
  { value: 'All', label: 'Todos os dias' },
  { value: 'Mon', label: 'Segunda' },
  { value: 'Tue', label: 'Terça' },
  { value: 'Wed', label: 'Quarta' },
  { value: 'Thu', label: 'Quinta' },
  { value: 'Fri', label: 'Sexta' },
  { value: 'Sat', label: 'Sábado' },
  { value: 'Sun', label: 'Domingo' },
];

const EVENT_TYPE_OPTIONS: { value: string; label: string }[] = [
  { value: 'Holiday', label: 'Feriado' },
  { value: 'Recess', label: 'Recesso' },
  { value: 'Custom', label: 'Personalizado' },
  { value: 'CollectiveVacation', label: 'Férias Coletivas' },
  { value: 'InternalFestivity', label: 'Festividade Interna' },
  { value: 'OptionalHoliday', label: 'Ponto Facultativo' },
  { value: 'Operational', label: 'Operacional' },
];

function parseWorkDaysCsv(csv: string): Set<string> {
  return new Set(
    (csv || '')
      .split(',')
      .map((s) => normalizeWorkDayToken(s.trim()))
      .filter(Boolean)
  );
}

function formatWorkDays(set: Set<string>): string {
  return WORK_DAY_KEYS.filter((k) => set.has(k)).join(',');
}

function timeToInput(ts: string | undefined): string {
  if (!ts) return '08:00';
  const m = /^(\d{1,2}):(\d{2})(?::(\d{2}))?/.exec(ts.trim());
  if (!m) return '08:00';
  const hh = m[1].padStart(2, '0');
  const mm = m[2].padStart(2, '0');
  return `${hh}:${mm}`;
}

function inputTimeToHms(value: string): string {
  const parts = value.split(':');
  const h = (parts[0] ?? '0').padStart(2, '0');
  const m = (parts[1] ?? '00').padStart(2, '0');
  return `${h}:${m}:00`;
}

function breakDayLabel(code: string): string {
  return BREAK_DAY_OPTIONS.find((o) => o.value === code)?.label ?? code;
}

function formatBreakSummary(b: CalendarBreakDto): string {
  return `${breakDayLabel(b.dayOfWeek)} — ${timeToInput(b.startTime)} às ${timeToInput(b.endTime)}`;
}

interface BigCalEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  allDay?: boolean;
  resource: {
    raw: CalendarEventDto;
    scope: string;
  };
}

function startOfDay(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function expandEventsToBigCalendar(
  events: CalendarEventDto[],
  rangeStart: Date,
  rangeEnd: Date
): BigCalEvent[] {
  const rs = startOfDay(rangeStart).getTime();
  const re = startOfDay(rangeEnd).getTime();
  const out: BigCalEvent[] = [];

  for (const ev of events) {
    const scope = (ev.scope ?? 'Customer').trim() || 'Customer';
    const base = moment(ev.eventDate);
    const month = base.month();
    const date = base.date();

    const pushInstance = (start: Date, suffix: string) => {
      const dayStart = startOfDay(start);
      const dayEnd = addDays(dayStart, 1);
      out.push({
        id: `${ev.id}${suffix}`,
        title: ev.name,
        start: dayStart,
        end: dayEnd,
        allDay: true,
        resource: { raw: ev, scope },
      });
    };

    if (ev.isRecurring) {
      const y0 = moment(rangeStart).year();
      const y1 = moment(rangeEnd).year();
      for (let y = y0; y <= y1; y++) {
        const inst = new Date(y, month, date);
        const t = startOfDay(inst).getTime();
        if (t >= rs && t <= re) {
          pushInstance(inst, `-${y}`);
        }
      }
    } else {
      const inst = base.toDate();
      const t = startOfDay(inst).getTime();
      if (t >= rs && t <= re) {
        pushInstance(inst, '');
      }
    }
  }

  return out;
}

function getCustomerIdFromStorage(): string {
  return sessionStorage.getItem('@KnowInDoc:customer_id')?.trim() ?? '';
}

const CalendarEventContent: React.FC<EventProps<BigCalEvent>> = ({ event }) => {
  const isGlobal = event.resource.scope === 'Global';
  return (
    <div className="flex flex-col gap-0.5 leading-tight">
      {isGlobal && (
        <span className="text-[9px] font-semibold uppercase tracking-wide text-white/90 bg-black/30 px-1 rounded w-fit">
          Global
        </span>
      )}
      <span className="truncate font-medium">{event.title}</span>
    </div>
  );
};

export const CalendarPage: React.FC = () => {
  const { user } = useAuth();
  const isPlatformAdmin = Boolean(user?.isAdmin);
  const customerId = useMemo(() => {
    const fromUser = user?.customer_id?.trim() ?? '';
    if (fromUser) return fromUser;
    return getCustomerIdFromStorage();
  }, [user?.customer_id]);

  const [pageLoading, setPageLoading] = useState(true);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [globalCalendar, setGlobalCalendar] = useState<CalendarDto | null>(null);
  const [customerCalendars, setCustomerCalendars] = useState<CalendarDto[]>([]);

  const [selection, setSelection] = useState<'global' | 'customer' | ''>('');
  const [selectedCustomerCalendarId, setSelectedCustomerCalendarId] = useState<string | null>(null);

  const [displayedCalendar, setDisplayedCalendar] = useState<CalendarDto | null>(null);
  const [breaks, setBreaks] = useState<CalendarBreakDto[]>([]);

  const [rangeStart, setRangeStart] = useState(() => moment().startOf('month').toDate());
  const [rangeEnd, setRangeEnd] = useState(() => moment().endOf('month').toDate());

  const [calendarForm, setCalendarForm] = useState({
    name: '',
    workDays: new Set<string>(['Mon', 'Tue', 'Wed', 'Thu', 'Fri']),
    workStartTime: '08:00',
    workEndTime: '18:00',
  });

  const [showCalendarCreateModal, setShowCalendarCreateModal] = useState(false);
  const [newCalendarName, setNewCalendarName] = useState('');
  const [newCalendarWorkDays, setNewCalendarWorkDays] = useState(
    () => new Set<string>(['Mon', 'Tue', 'Wed', 'Thu', 'Fri'])
  );
  const [newCalendarStart, setNewCalendarStart] = useState('08:00');
  const [newCalendarEnd, setNewCalendarEnd] = useState('18:00');

  const [eventModalOpen, setEventModalOpen] = useState(false);
  const [eventSaving, setEventSaving] = useState(false);
  const [eventForm, setEventForm] = useState({
    id: '' as string | null,
    name: '',
    eventDate: '',
    type: 'Holiday',
    isRecurring: false,
    scope: 'Customer',
    calendarIdForApi: '' as string,
    readOnly: false,
  });

  const [breaksOpen, setBreaksOpen] = useState(false);

  const [breakModalOpen, setBreakModalOpen] = useState(false);
  const [breakSaving, setBreakSaving] = useState(false);
  const [editingBreakId, setEditingBreakId] = useState<string | null>(null);
  const [breakForm, setBreakForm] = useState({
    dayOfWeek: 'All',
    startTime: '12:00',
    endTime: '13:00',
  });

  const showError = useCallback((msg: string) => {
    setError(msg);
  }, []);

  const loadLists = useCallback(async (): Promise<{ list: CalendarDto[]; global: CalendarDto | null } | null> => {
    if (!customerId) {
      setCustomerCalendars([]);
      setGlobalCalendar(null);
      return null;
    }
    const [list, global] = await Promise.all([
      calendarService.getCalendars(customerId),
      calendarService.getGlobalCalendar(),
    ]);
    setCustomerCalendars(list);
    setGlobalCalendar(global);
    return { list, global };
  }, [customerId]);

  const applySelectionAfterListLoad = useCallback((list: CalendarDto[], global: CalendarDto | null) => {
    if (list.length > 0) {
      setSelection('customer');
      setSelectedCustomerCalendarId(list[0].id);
    } else if (global) {
      setSelection('global');
      setSelectedCustomerCalendarId(null);
    } else {
      setSelection('');
      setSelectedCustomerCalendarId(null);
    }
  }, []);

  const loadDetail = useCallback(async () => {
    if (!customerId) return;

    if (!selection) {
      setDisplayedCalendar(null);
      setBreaks([]);
      return;
    }

    if (selection === 'global' && globalCalendar) {
      setDetailLoading(true);
      try {
        const fresh = await calendarService.getCalendarById(globalCalendar.id);
        setDisplayedCalendar(fresh);
        const br = await calendarService.getBreaks(globalCalendar.id);
        setBreaks(br);
      } catch (e) {
        showError(e instanceof Error ? e.message : 'Erro ao carregar calendário global.');
      } finally {
        setDetailLoading(false);
      }
      return;
    }

    if (selection === 'customer' && selectedCustomerCalendarId) {
      setDetailLoading(true);
      try {
        const eff = await calendarService.getEffectiveCalendar(selectedCustomerCalendarId, customerId);
        setDisplayedCalendar(eff);
        const br = await calendarService.getBreaks(selectedCustomerCalendarId);
        setBreaks(br);
      } catch (e) {
        showError(e instanceof Error ? e.message : 'Erro ao carregar calendário.');
      } finally {
        setDetailLoading(false);
      }
      return;
    }

    setDisplayedCalendar(null);
    setBreaks([]);
  }, [customerId, globalCalendar, selectedCustomerCalendarId, selection, showError]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!customerId) {
        setPageLoading(false);
        setError('Cliente não identificado na sessão. Faça login novamente.');
        return;
      }
      setPageLoading(true);
      setError(null);
      try {
        const [list, global] = await Promise.all([
          calendarService.getCalendars(customerId),
          calendarService.getGlobalCalendar(),
        ]);
        if (cancelled) return;
        setCustomerCalendars(list);
        setGlobalCalendar(global);
        applySelectionAfterListLoad(list, global);
      } catch (e) {
        if (!cancelled) {
          showError(e instanceof Error ? e.message : 'Erro ao carregar calendários.');
        }
      } finally {
        if (!cancelled) setPageLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [customerId, showError, applySelectionAfterListLoad]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    if (!displayedCalendar) return;
    setCalendarForm({
      name: displayedCalendar.name,
      workDays: parseWorkDaysCsv(displayedCalendar.workDays),
      workStartTime: timeToInput(displayedCalendar.workStartTime),
      workEndTime: timeToInput(displayedCalendar.workEndTime),
    });
  }, [
    displayedCalendar?.id,
    displayedCalendar?.workDays,
    displayedCalendar?.name,
    displayedCalendar?.workStartTime,
    displayedCalendar?.workEndTime,
  ]);

  const viewingGlobal = selection === 'global';
  const globalReadOnly = viewingGlobal && !isPlatformAdmin;
  const calendarMetaReadOnly = viewingGlobal;
  const breaksEditable = selection === 'customer' && Boolean(selectedCustomerCalendarId);
  const eventsSelectable =
    (viewingGlobal && isPlatformAdmin) || (selection === 'customer' && Boolean(selectedCustomerCalendarId));

  const bigCalendarEvents = useMemo(() => {
    if (!displayedCalendar?.events) return [];
    return expandEventsToBigCalendar(displayedCalendar.events, rangeStart, rangeEnd);
  }, [displayedCalendar, rangeStart, rangeEnd]);

  const handleRangeChange = useCallback((range: Date[] | { start: Date; end: Date }) => {
    if (Array.isArray(range)) {
      if (range.length > 0) {
        setRangeStart(moment(range[0]).startOf('day').toDate());
        setRangeEnd(moment(range[range.length - 1]).endOf('day').toDate());
      }
    } else if (range?.start && range?.end) {
      setRangeStart(moment(range.start).startOf('day').toDate());
      setRangeEnd(moment(range.end).endOf('day').toDate());
    }
  }, []);

  const refreshAll = async () => {
    await loadLists();
    await loadDetail();
  };

  const onSelectCalendar = async (value: string) => {
    if (value.startsWith('global:')) {
      setSelection('global');
      setSelectedCustomerCalendarId(null);
    } else if (value.startsWith('customer:')) {
      setSelection('customer');
      setSelectedCustomerCalendarId(value.replace('customer:', ''));
    }
  };

  const saveCalendarMeta = async () => {
    if (!selectedCustomerCalendarId || calendarMetaReadOnly) return;
    setDetailLoading(true);
    setError(null);
    try {
      await calendarService.updateCalendar(selectedCustomerCalendarId, {
        name: calendarForm.name.trim(),
        workDays: formatWorkDays(calendarForm.workDays),
        workStartTime: inputTimeToHms(calendarForm.workStartTime),
        workEndTime: inputTimeToHms(calendarForm.workEndTime),
      });
      await refreshAll();
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Erro ao salvar.');
    } finally {
      setDetailLoading(false);
    }
  };

  const deleteSelectedCalendar = async () => {
    if (!selectedCustomerCalendarId) return;
    if (!window.confirm('Excluir este calendário e seus dados?')) return;
    setDetailLoading(true);
    setError(null);
    try {
      await calendarService.deleteCalendar(selectedCustomerCalendarId);
      const fresh = await loadLists();
      if (fresh) applySelectionAfterListLoad(fresh.list, fresh.global);
      else {
        setSelection('');
        setSelectedCustomerCalendarId(null);
      }
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Erro ao excluir.');
    } finally {
      setDetailLoading(false);
    }
  };

  const createCalendar = async () => {
    if (!customerId || !newCalendarName.trim()) return;
    setDetailLoading(true);
    setError(null);
    try {
      const id = await calendarService.createCalendar({
        customerId,
        name: newCalendarName.trim(),
        workDays: formatWorkDays(newCalendarWorkDays),
        workStartTime: inputTimeToHms(newCalendarStart),
        workEndTime: inputTimeToHms(newCalendarEnd),
      });
      setShowCalendarCreateModal(false);
      setNewCalendarName('');
      await loadLists();
      setSelection('customer');
      setSelectedCustomerCalendarId(id);
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Erro ao criar calendário.');
    } finally {
      setDetailLoading(false);
    }
  };

  const openNewEventModal = (date?: Date) => {
    const d = date ? moment(date) : moment();
    const calId =
      viewingGlobal && globalCalendar
        ? globalCalendar.id
        : selectedCustomerCalendarId ?? '';
    if (!calId) return;
    setEventForm({
      id: null,
      name: '',
      eventDate: d.format('YYYY-MM-DD'),
      type: 'Holiday',
      isRecurring: false,
      scope: 'Customer',
      calendarIdForApi: calId,
      readOnly: false,
    });
    setEventModalOpen(true);
  };

  const openEditEventModal = (ev: BigCalEvent) => {
    const raw = ev.resource.raw;
    const scope = ev.resource.scope;
    const readOnly = scope === 'Global' && !isPlatformAdmin;
    setEventForm({
      id: raw.id,
      name: raw.name,
      eventDate: moment(raw.eventDate).format('YYYY-MM-DD'),
      type: raw.type,
      isRecurring: raw.isRecurring,
      scope,
      calendarIdForApi: raw.calendarId,
      readOnly,
    });
    setEventModalOpen(true);
  };

  const saveEvent = async () => {
    if (eventForm.readOnly || !eventForm.name.trim()) return;
    setEventSaving(true);
    setError(null);
    try {
      const bodyDate = moment(eventForm.eventDate).format('YYYY-MM-DD');
      if (eventForm.id) {
        await calendarService.updateCalendarEvent(eventForm.calendarIdForApi, eventForm.id, {
          name: eventForm.name.trim(),
          eventDate: bodyDate,
          type: eventForm.type,
          isRecurring: eventForm.isRecurring,
        });
      } else {
        await calendarService.addCalendarEvent(eventForm.calendarIdForApi, {
          name: eventForm.name.trim(),
          eventDate: bodyDate,
          type: eventForm.type,
          isRecurring: eventForm.isRecurring,
        });
      }
      setEventModalOpen(false);
      await refreshAll();
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Erro ao salvar evento.');
    } finally {
      setEventSaving(false);
    }
  };

  const deleteEvent = async () => {
    if (!eventForm.id || eventForm.readOnly) return;
    if (!window.confirm('Excluir este evento?')) return;
    setEventSaving(true);
    setError(null);
    try {
      await calendarService.deleteCalendarEvent(eventForm.calendarIdForApi, eventForm.id);
      setEventModalOpen(false);
      await refreshAll();
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Erro ao excluir evento.');
    } finally {
      setEventSaving(false);
    }
  };

  const openBreakModal = (b?: CalendarBreakDto) => {
    if (b) {
      setEditingBreakId(b.id);
      setBreakForm({
        dayOfWeek: b.dayOfWeek,
        startTime: timeToInput(b.startTime),
        endTime: timeToInput(b.endTime),
      });
    } else {
      setEditingBreakId(null);
      setBreakForm({ dayOfWeek: 'All', startTime: '12:00', endTime: '13:00' });
    }
    setBreakModalOpen(true);
  };

  const saveBreak = async () => {
    if (!selectedCustomerCalendarId) return;
    setBreakSaving(true);
    setError(null);
    try {
      const payload = {
        dayOfWeek: breakForm.dayOfWeek,
        startTime: inputTimeToHms(breakForm.startTime),
        endTime: inputTimeToHms(breakForm.endTime),
      };
      if (editingBreakId) {
        await calendarService.updateBreak(selectedCustomerCalendarId, editingBreakId, payload);
      } else {
        await calendarService.addBreak(selectedCustomerCalendarId, payload);
      }
      setBreakModalOpen(false);
      const br = await calendarService.getBreaks(selectedCustomerCalendarId);
      setBreaks(br);
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Erro ao salvar intervalo.');
    } finally {
      setBreakSaving(false);
    }
  };

  const deleteBreak = async (id: string) => {
    if (!selectedCustomerCalendarId) return;
    if (!window.confirm('Remover este intervalo?')) return;
    setError(null);
    try {
      await calendarService.deleteBreak(selectedCustomerCalendarId, id);
      const br = await calendarService.getBreaks(selectedCustomerCalendarId);
      setBreaks(br);
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Erro ao excluir intervalo.');
    }
  };

  if (pageLoading) {
    return (
      <div className="p-6 space-y-4">
        <div className="h-8 w-64 bg-gray-200 rounded animate-pulse" />
        <div className="h-[600px] bg-gray-100 rounded-lg animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      <div className="flex flex-wrap justify-between items-center gap-4">
        <h1 className="text-2xl font-bold">Calendário Corporativo</h1>
        <div className="flex gap-2">
          <Button
            variant="primary"
            size="sm"
            onClick={() => openNewEventModal()}
            disabled={
              (!viewingGlobal || !globalCalendar || !isPlatformAdmin) &&
              (selection !== 'customer' || !selectedCustomerCalendarId)
            }
          >
            Novo evento
          </Button>
        </div>
      </div>

      {error && (
        <div
          className="rounded-md bg-red-50 text-red-800 px-4 py-3 text-sm flex justify-between gap-4"
          role="alert"
        >
          <span>{error}</span>
          <button type="button" className="shrink-0 underline font-medium" onClick={() => setError(null)}>
            Fechar
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-12 gap-6">
        <div className="xl:col-span-4 space-y-6">
          <div className="bg-white rounded-lg shadow p-4 space-y-3">
            <div className="flex items-center justify-between">
              <h2 className="text-sm font-medium text-gray-700">Calendários</h2>
              {customerId && (
                <button
                  type="button"
                  className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 disabled:opacity-50"
                  disabled={detailLoading}
                  onClick={() => setShowCalendarCreateModal(true)}
                >
                  <span className="text-base leading-none">+</span> Novo
                </button>
              )}
            </div>
            <ul className="space-y-1.5">
              {globalCalendar && (
                <li>
                  <button
                    type="button"
                    disabled={detailLoading}
                    onClick={() => void onSelectCalendar(`global:${globalCalendar.id}`)}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-left transition-colors ${
                      selection === 'global'
                        ? 'bg-blue-50 border border-blue-300'
                        : 'bg-gray-50 border border-transparent hover:bg-gray-100'
                    }`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <svg className="h-4 w-4 shrink-0 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                      </svg>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">{globalCalendar.name}</p>
                        <p className="text-xs text-gray-500 truncate">{formatWorkDaysDisplay(globalCalendar.workDays)}</p>
                      </div>
                    </div>
                    <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded">
                      Global
                    </span>
                  </button>
                </li>
              )}
              {customerCalendars.map((c) => (
                <li key={c.id}>
                  <button
                    type="button"
                    disabled={detailLoading}
                    onClick={() => void onSelectCalendar(`customer:${c.id}`)}
                    className={`w-full flex items-center justify-between gap-2 px-3 py-2.5 rounded-lg text-left transition-colors ${
                      selection === 'customer' && selectedCustomerCalendarId === c.id
                        ? 'bg-blue-50 border border-blue-300'
                        : 'bg-gray-50 border border-transparent hover:bg-gray-100'
                    }`}
                  >
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{c.name}</p>
                      <p className="text-xs text-gray-500 truncate">{formatWorkDaysDisplay(c.workDays)}</p>
                    </div>
                    <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                      Cliente
                    </span>
                  </button>
                </li>
              ))}
              {customerCalendars.length === 0 && !detailLoading && (
                <li className="text-center py-3 text-sm text-gray-500">
                  Nenhum calendário criado.
                  {customerId && (
                    <button
                      type="button"
                      className="block mx-auto mt-1 text-blue-600 hover:underline text-xs font-medium"
                      onClick={() => setShowCalendarCreateModal(true)}
                    >
                      Criar calendário
                    </button>
                  )}
                </li>
              )}
            </ul>
            {detailLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <span className="inline-block h-4 w-4 border-2 border-gray-300 border-t-blue-600 rounded-full animate-spin" />
                Carregando detalhes…
              </div>
            )}
            {globalReadOnly && selection === 'global' && (
              <p className="text-xs text-gray-600">
                O calendário global é somente leitura. Crie eventos no seu calendário para a mesma data a fim de
                substituir feriados globais quando permitido.
              </p>
            )}
          </div>

          {displayedCalendar && (
            <div className="bg-white rounded-lg shadow p-4 space-y-4">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-semibold text-gray-800 truncate">{displayedCalendar.name}</h2>
                {viewingGlobal ? (
                  <span className="flex items-center gap-1 shrink-0 text-[10px] font-semibold uppercase tracking-wide bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded">
                    <svg className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    </svg>
                    Global · somente leitura
                  </span>
                ) : (
                  <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wide bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                    Cliente
                  </span>
                )}
              </div>
              {calendarMetaReadOnly ? (
                <div className="text-sm text-gray-600 space-y-1">
                  <p>Dias úteis: <strong>{formatWorkDaysDisplay(displayedCalendar.workDays)}</strong></p>
                  <p>
                    Expediente:{' '}
                    <strong>
                      {timeToInput(displayedCalendar.workStartTime)} — {timeToInput(displayedCalendar.workEndTime)}
                    </strong>
                  </p>
                </div>
              ) : (
                <>
                  <Input
                    label="Nome"
                    value={calendarForm.name}
                    onChange={(e) => setCalendarForm((f) => ({ ...f, name: e.target.value }))}
                  />
                  <fieldset>
                    <legend className="text-sm font-medium text-gray-700 mb-2">Dias úteis</legend>
                    <div className="flex flex-wrap gap-3">
                      {WORK_DAY_KEYS.map((k) => (
                        <label key={k} className="inline-flex items-center gap-1 text-sm">
                          <input
                            type="checkbox"
                            checked={calendarForm.workDays.has(k)}
                            onChange={() => {
                              setCalendarForm((f) => {
                                const next = new Set(f.workDays);
                                if (next.has(k)) next.delete(k);
                                else next.add(k);
                                return { ...f, workDays: next };
                              });
                            }}
                          />
                          {WORK_DAY_LABELS[k]}
                        </label>
                      ))}
                    </div>
                  </fieldset>
                  <div className="grid grid-cols-2 gap-3">
                    <Input
                      label="Horário início"
                      type="time"
                      value={calendarForm.workStartTime}
                      onChange={(e) => setCalendarForm((f) => ({ ...f, workStartTime: e.target.value }))}
                    />
                    <Input
                      label="Horário fim"
                      type="time"
                      value={calendarForm.workEndTime}
                      onChange={(e) => setCalendarForm((f) => ({ ...f, workEndTime: e.target.value }))}
                    />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <Button variant="primary" size="sm" onClick={() => void saveCalendarMeta()}>
                      Salvar calendário
                    </Button>
                    <Button variant="danger" size="sm" onClick={() => void deleteSelectedCalendar()}>
                      Excluir calendário
                    </Button>
                  </div>
                </>
              )}
            </div>
          )}

          {displayedCalendar && (
            <div className="bg-white rounded-lg shadow p-4 space-y-3">
              <button
                type="button"
                className="w-full flex items-center justify-between gap-2"
                onClick={() => setBreaksOpen((o) => !o)}
              >
                <h2 className="text-lg font-semibold text-gray-800">
                  Intervalos{breaks.length > 0 ? ` (${breaks.length})` : ''}
                </h2>
                <svg
                  className={`h-5 w-5 text-gray-500 transition-transform duration-200 ${breaksOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {breaksOpen && (
                <>
                  {!breaksEditable && (
                    <p className="text-xs text-gray-600">
                      Intervalos são configurados apenas nos calendários do cliente. O calendário global não recebe
                      intervalos por esta API.
                    </p>
                  )}
                  {breaksEditable && (
                    <Button variant="secondary" size="sm" onClick={() => openBreakModal()}>
                      Adicionar intervalo
                    </Button>
                  )}
                  {breaks.length === 0 ? (
                    <p className="text-sm text-gray-500">Nenhum intervalo cadastrado.</p>
                  ) : (
                    <ul className="space-y-2">
                      {breaks.map((b) => (
                        <li
                          key={b.id}
                          className="flex justify-between items-center text-sm border border-gray-100 rounded-md px-3 py-2"
                        >
                          <span>{formatBreakSummary(b)}</span>
                          {breaksEditable && (
                            <span className="flex gap-2 shrink-0">
                              <button
                                type="button"
                                className="text-blue-600 hover:underline text-xs"
                                onClick={() => openBreakModal(b)}
                              >
                                Editar
                              </button>
                              <button
                                type="button"
                                className="text-red-600 hover:underline text-xs"
                                onClick={() => void deleteBreak(b.id)}
                              >
                                Excluir
                              </button>
                            </span>
                          )}
                        </li>
                      ))}
                    </ul>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        <div className="xl:col-span-8 bg-white rounded-lg shadow p-4 space-y-3">
          {displayedCalendar && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <span>Visualizando:</span>
              <span className="font-semibold text-gray-800">{displayedCalendar.name}</span>
              {viewingGlobal ? (
                <span className="text-[10px] font-semibold uppercase tracking-wide bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded">
                  Global
                </span>
              ) : (
                <span className="text-[10px] font-semibold uppercase tracking-wide bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded">
                  Cliente
                </span>
              )}
            </div>
          )}
          <Calendar
            localizer={localizer}
            culture="pt-BR"
            events={bigCalendarEvents}
            startAccessor="start"
            endAccessor="end"
            style={{ height: 620 }}
            selectable={eventsSelectable}
            onSelectSlot={(slot) => {
              if (!eventsSelectable) return;
              openNewEventModal(slot.start as Date);
            }}
            onSelectEvent={(ev) => openEditEventModal(ev as BigCalEvent)}
            onRangeChange={handleRangeChange}
            components={{ event: CalendarEventContent }}
            eventPropGetter={(ev) => {
              const e = ev as BigCalEvent;
              const global = e.resource?.scope === 'Global';
              return {
                style: {
                  backgroundColor: global ? '#6b7280' : '#2563eb',
                  borderRadius: 4,
                  color: '#fff',
                  border: 'none',
                },
              };
            }}
            messages={{
              next: 'Próximo',
              previous: 'Anterior',
              today: 'Hoje',
              month: 'Mês',
              week: 'Semana',
              day: 'Dia',
              agenda: 'Agenda',
              date: 'Data',
              time: 'Hora',
              event: 'Evento',
              noEventsInRange: 'Não há eventos neste período.',
              allDay: 'Dia inteiro',
              work_week: 'Semana de trabalho',
              yesterday: 'Ontem',
              tomorrow: 'Amanhã',
              showMore: (total: number) => `+ ${total} mais`,
            }}
          />
        </div>
      </div>

      <Modal isOpen={showCalendarCreateModal} onClose={() => setShowCalendarCreateModal(false)} title="Novo calendário">
        <div className="space-y-4">
          <Input label="Nome" value={newCalendarName} onChange={(e) => setNewCalendarName(e.target.value)} required />
          <fieldset>
            <legend className="text-sm font-medium text-gray-700 mb-2">Dias úteis</legend>
            <div className="flex flex-wrap gap-3">
              {WORK_DAY_KEYS.map((k) => (
                <label key={k} className="inline-flex items-center gap-1 text-sm">
                  <input
                    type="checkbox"
                    checked={newCalendarWorkDays.has(k)}
                    onChange={() => {
                      setNewCalendarWorkDays((prev) => {
                        const next = new Set(prev);
                        if (next.has(k)) next.delete(k);
                        else next.add(k);
                        return next;
                      });
                    }}
                  />
                  {WORK_DAY_LABELS[k]}
                </label>
              ))}
            </div>
          </fieldset>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Horário início"
              type="time"
              value={newCalendarStart}
              onChange={(e) => setNewCalendarStart(e.target.value)}
            />
            <Input
              label="Horário fim"
              type="time"
              value={newCalendarEnd}
              onChange={(e) => setNewCalendarEnd(e.target.value)}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setShowCalendarCreateModal(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={() => void createCalendar()} disabled={!newCalendarName.trim()}>
              Criar
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={eventModalOpen}
        onClose={() => setEventModalOpen(false)}
        title={eventForm.id ? 'Evento' : 'Novo evento'}
      >
        <div className="space-y-4">
          {eventForm.readOnly && (
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded px-3 py-2">
              Evento global: somente leitura. Para sobrescrever, crie um evento no seu calendário com a mesma data e
              nome.
            </p>
          )}
          <Input
            label="Nome"
            value={eventForm.name}
            onChange={(e) => setEventForm((f) => ({ ...f, name: e.target.value }))}
            disabled={eventForm.readOnly}
          />
          <Input
            label="Data"
            type="date"
            value={eventForm.eventDate}
            onChange={(e) => setEventForm((f) => ({ ...f, eventDate: e.target.value }))}
            disabled={eventForm.readOnly}
          />
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
            <select
              className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm"
              value={eventForm.type}
              onChange={(e) => setEventForm((f) => ({ ...f, type: e.target.value }))}
              disabled={eventForm.readOnly}
            >
              {EVENT_TYPE_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={eventForm.isRecurring}
              onChange={(e) => setEventForm((f) => ({ ...f, isRecurring: e.target.checked }))}
              disabled={eventForm.readOnly}
            />
            Recorrente (anual)
          </label>
          <div className="flex justify-end flex-wrap gap-2 pt-4">
            {eventForm.id && !eventForm.readOnly && (
              <Button variant="danger" onClick={() => void deleteEvent()} disabled={eventSaving}>
                Excluir
              </Button>
            )}
            <Button variant="secondary" onClick={() => setEventModalOpen(false)}>
              Fechar
            </Button>
            {!eventForm.readOnly && (
              <Button
                variant="primary"
                onClick={() => void saveEvent()}
                disabled={eventSaving || !eventForm.name.trim()}
              >
                {eventSaving ? 'Salvando…' : 'Salvar'}
              </Button>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        isOpen={breakModalOpen}
        onClose={() => setBreakModalOpen(false)}
        title={editingBreakId ? 'Editar intervalo' : 'Novo intervalo'}
      >
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Dia da semana</label>
            <select
              className="block w-full border border-gray-300 rounded-md py-2 px-3 text-sm"
              value={breakForm.dayOfWeek}
              onChange={(e) => setBreakForm((f) => ({ ...f, dayOfWeek: e.target.value }))}
            >
              {BREAK_DAY_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Input
              label="Hora início"
              type="time"
              value={breakForm.startTime}
              onChange={(e) => setBreakForm((f) => ({ ...f, startTime: e.target.value }))}
            />
            <Input
              label="Hora fim"
              type="time"
              value={breakForm.endTime}
              onChange={(e) => setBreakForm((f) => ({ ...f, endTime: e.target.value }))}
            />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={() => setBreakModalOpen(false)}>
              Cancelar
            </Button>
            <Button variant="primary" onClick={() => void saveBreak()} disabled={breakSaving}>
              {breakSaving ? 'Salvando…' : 'Salvar'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
